/**
 * TrackGenerator.js - Programmatic Track Generation System
 * 
 * Generates wide, navigable tracks programmatically to prevent player stuck issues.
 * Creates collision data directly without relying on pixel reading.
 */

export class TrackGenerator {
    constructor(width = 1920, height = 1080) {
        this.width = width;
        this.height = height;
        
        // FULL SCREEN - No margins for complete coverage
        this.marginX = 0;
        this.marginY = 0;  
        this.safeWidth = width; // Full width
        this.safeHeight = height; // Full height
        
        // Safe area bounds for track generation (full screen)
        this.minX = 0;
        this.maxX = width;
        this.minY = 0;
        this.maxY = height;
        
        this.trackWidth = 700; // Wide open tracks for 6 players
        this.validPositions = [];
        this.trackData = null;
        this.selectedBackground = null;
        this.positionGrid = null; // Spatial grid for fast lookups
        
        // Available background images - just the map numbers
        this.backgroundImages = [
            '1', '2', '3', '4', '5', '6', '8', '9', '10',
            '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21'
        ];
    }

    /**
     * Generate a complete map with background and track
     */
    generateCompleteMap(graphics, scene) {
        // Select random background image
        this.selectedBackground = this.backgroundImages[Math.floor(Math.random() * this.backgroundImages.length)];
        console.log(`[TrackGenerator] Selected background: ${this.selectedBackground}`);
        
        // Load and draw background image if scene is provided
        if (scene) {
            this.drawBackgroundImage(scene);
        } else {
            // Fallback to simple colored background
            this.drawSimpleBackground(graphics);
        }
        
        // Generate track data
        this.trackData = this.generateRandomTrack();
        
        // Build spatial grid for fast collision detection
        this.buildSpatialGrid();
        
        // Draw track on background
        this.drawCompleteTrack(graphics);
        
        return this.trackData;
    }
    
    /**
     * Draw background image from assets
     */
    drawBackgroundImage(scene) {
        if (!scene || !this.selectedBackground) return;
        
        // Create background image sprite using the correct texture key
        const backgroundPath = `backgrounds/map${this.selectedBackground}`;
        const bg = scene.add.image(this.width / 2, this.height / 2, backgroundPath);
        
        // Scale to fit the game area
        bg.setDisplaySize(this.width, this.height);
        bg.setDepth(0); // Background layer - behind track graphics
        
        console.log(`[TrackGenerator] Background image loaded: ${backgroundPath}`);
    }
    
    /**
     * Draw simple colored background as fallback
     */
    drawSimpleBackground(graphics) {
        if (!graphics) return;
        
        // Simple gray background
        graphics.fillStyle(0x1a3a1a, 1);
        graphics.fillRect(0, 0, this.width, this.height);
    }
    
    /**
     * Draw the complete track with road surface
     */
    drawCompleteTrack(graphics) {
        if (!graphics) return;
        
        // NO VISUAL CONSTRAINTS - players can move freely anywhere
        // No track elements, no borders, no guides - completely free movement on entire screen
    }
    
    /**
     * Draw visual track boundaries for clarity
     */
    drawTrackBoundaries(graphics) {
        // Draw red boundary markers at track edges
        graphics.lineStyle(3, 0xff0000, 0.5); // Red boundary line
        
        // Sample track edges and draw boundary indicators
        const step = Math.max(1, Math.floor(this.validPositions.length / 100));
        for (let i = 0; i < this.validPositions.length; i += step) {
            const pos = this.validPositions[i];
            
            // Check if this position is near an edge
            let edgeCount = 0;
            const testRadius = 50;
            const angles = [0, Math.PI/2, Math.PI, Math.PI*1.5];
            
            for (const angle of angles) {
                const testX = pos.x + Math.cos(angle) * testRadius;
                const testY = pos.y + Math.sin(angle) * testRadius;
                if (!this.isPositionValid(testX, testY)) {
                    edgeCount++;
                }
            }
            
            // If near edge, draw a boundary indicator
            if (edgeCount >= 2) {
                graphics.strokeCircle(pos.x, pos.y, 30);
            }
        }
    }
    
    /**
     * Draw track markings (lines, arrows, etc.)
     */
    drawTrackMarkings(graphics) {
        // Draw track boundary visualization for debugging
        this.drawTrackBoundaries(graphics);
        
        // White edge lines removed as requested
        // graphics.lineStyle(6, 0xffffff, 1); // Removed white boundaries
        
        // Yellow center line (dashed) - more visible
        graphics.lineStyle(4, 0xffff00, 1); // Thicker, fully opaque yellow
        const centerLine = this.getTrackCenterLine();
        for (let i = 0; i < centerLine.length - 1; i += 2) {
            graphics.beginPath();
            graphics.moveTo(centerLine[i].x, centerLine[i].y);
            if (centerLine[i + 1]) {
                graphics.lineTo(centerLine[i + 1].x, centerLine[i + 1].y);
            }
            graphics.strokePath();
        }
        
        // Lane dividers removed as requested
        // this.drawLaneDividers(graphics);
    }
    
    /**
     * Draw lane dividers for wide track
     */
    drawLaneDividers(graphics) {
        graphics.lineStyle(2, 0xffffff, 0.3); // Subtle white lane lines
        
        // Draw lane dividers at regular intervals
        const step = Math.max(1, Math.floor(this.validPositions.length / 200));
        const laneWidth = 100; // Each lane is 100px wide
        const numLanes = Math.floor(this.trackWidth / laneWidth);
        
        for (let lane = 1; lane < numLanes; lane++) {
            const offset = (lane - numLanes/2) * laneWidth;
            
            for (let i = 0; i < this.validPositions.length; i += step * 3) { // Dashed lines
                const pos = this.validPositions[i];
                const nextPos = this.validPositions[Math.min(i + step, this.validPositions.length - 1)];
                
                // Calculate perpendicular direction for lane offset
                const dx = nextPos.x - pos.x;
                const dy = nextPos.y - pos.y;
                const length = Math.sqrt(dx * dx + dy * dy);
                
                if (length > 0) {
                    const perpX = -dy / length;
                    const perpY = dx / length;
                    
                    const startX = pos.x + perpX * offset;
                    const startY = pos.y + perpY * offset;
                    const endX = nextPos.x + perpX * offset;
                    const endY = nextPos.y + perpY * offset;
                    
                    graphics.beginPath();
                    graphics.moveTo(startX, startY);
                    graphics.lineTo(endX, endY);
                    graphics.strokePath();
                }
            }
        }
    }
    
    /**
     * Draw environmental decorations (deprecated - using background images now)
     */
    drawEnvironment(graphics) {
        if (!graphics) return;
        
        switch (this.theme) {
            case 'desert':
                this.drawDesertElements(graphics);
                break;
            case 'forest':
                this.drawForestElements(graphics);
                break;
            case 'city':
                this.drawCityElements(graphics);
                break;
            case 'mountain':
                this.drawMountainElements(graphics);
                break;
            case 'beach':
                this.drawBeachElements(graphics);
                break;
        }
    }
    
    /**
     * Draw desert theme elements
     */
    drawDesertElements(graphics) {
        // Cacti
        graphics.fillStyle(0x2d5016, 0.8);
        for (let i = 0; i < 15; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            if (!this.isPositionValid(x, y)) {
                graphics.fillRect(x - 5, y - 20, 10, 40);
                graphics.fillRect(x - 15, y - 10, 30, 10);
            }
        }
        
        // Rocks
        graphics.fillStyle(0x8b7355, 0.6);
        for (let i = 0; i < 20; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            if (!this.isPositionValid(x, y)) {
                graphics.fillCircle(x, y, 15 + Math.random() * 10);
            }
        }
    }
    
    /**
     * Draw forest theme elements
     */
    drawForestElements(graphics) {
        // Trees
        for (let i = 0; i < 30; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            if (!this.isPositionValid(x, y)) {
                // Tree trunk
                graphics.fillStyle(0x4a3426, 0.9);
                graphics.fillRect(x - 10, y - 10, 20, 20);
                // Tree top
                graphics.fillStyle(0x0d5016, 0.8);
                graphics.fillCircle(x, y - 20, 25);
            }
        }
    }
    
    /**
     * Draw city theme elements
     */
    drawCityElements(graphics) {
        // Buildings
        graphics.fillStyle(0x5a5a5a, 0.7);
        for (let i = 0; i < 10; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            const w = 60 + Math.random() * 40;
            const h = 80 + Math.random() * 60;
            if (!this.isPositionValid(x, y)) {
                graphics.fillRect(x - w/2, y - h/2, w, h);
                // Windows
                graphics.fillStyle(0xffff88, 0.5);
                for (let wx = x - w/2 + 10; wx < x + w/2 - 10; wx += 20) {
                    for (let wy = y - h/2 + 10; wy < y + h/2 - 10; wy += 20) {
                        graphics.fillRect(wx, wy, 8, 8);
                    }
                }
                graphics.fillStyle(0x5a5a5a, 0.7);
            }
        }
    }
    
    /**
     * Draw mountain theme elements
     */
    drawMountainElements(graphics) {
        // Rock formations
        graphics.fillStyle(0x6a5a4a, 0.8);
        for (let i = 0; i < 25; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            if (!this.isPositionValid(x, y)) {
                const size = 20 + Math.random() * 30;
                graphics.fillCircle(x, y, size);
                graphics.fillCircle(x + size/2, y, size * 0.8);
                graphics.fillCircle(x - size/2, y, size * 0.8);
            }
        }
    }
    
    /**
     * Draw beach theme elements
     */
    drawBeachElements(graphics) {
        // Water areas
        graphics.fillStyle(0x4a8fe7, 0.4);
        for (let i = 0; i < 8; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            if (!this.isPositionValid(x, y)) {
                graphics.fillCircle(x, y, 60 + Math.random() * 40);
            }
        }
        
        // Palm trees
        for (let i = 0; i < 12; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            if (!this.isPositionValid(x, y)) {
                // Trunk
                graphics.fillStyle(0x8b6331, 0.9);
                graphics.fillRect(x - 8, y - 15, 16, 30);
                // Leaves
                graphics.fillStyle(0x2d7016, 0.8);
                graphics.fillCircle(x, y - 25, 20);
            }
        }
    }
    
    /**
     * Generate a random track type
     */
    generateRandomTrack() {
        // Always generate rectangular free-movement area instead of constrained tracks
        console.log(`[TrackGenerator] Generating rectangular free-movement area`);
        return this.generateRectangularTrack();
    }
    
    /**
     * Generate a rectangular track that fills the entire safe area
     * This allows free movement instead of constraining to paths
     */
    generateRectangularTrack() {
        this.validPositions = [];
        
        // Fill entire safe area with valid positions for free movement
        // Use larger steps for performance but ensure full coverage
        const step = 30;
        for (let x = this.minX; x <= this.maxX; x += step) {
            for (let y = this.minY; y <= this.maxY; y += step) {
                this.validPositions.push({ x, y });
            }
        }
        
        console.log(`[TrackGenerator] Generated ${this.validPositions.length} valid positions in rectangular area`);
        console.log(`[TrackGenerator] Safe area: (${this.minX}, ${this.minY}) to (${this.maxX}, ${this.maxY})`);
        
        return {
            type: 'rectangular',
            validPositions: this.validPositions,
            startZone: { x: this.width / 2, y: this.height / 2 },
            trackWidth: this.trackWidth
        };
    }

    /**
     * Generate an oval track
     */
    generateOvalTrack() {
        this.validPositions = [];
        const centerX = this.width / 2;
        const centerY = this.height / 2;
        // Reduced radius to fit in 80% area
        const radiusX = 480; // 80% of 600
        const radiusY = 320; // 80% of 400
        
        // Generate points along oval path - much less dense
        for (let angle = 0; angle < Math.PI * 2; angle += 0.05) { // Increased from 0.01
            const baseX = centerX + Math.cos(angle) * radiusX;
            const baseY = centerY + Math.sin(angle) * radiusY;
            
            // Add width to the track - DENSE sampling for continuous coverage
            for (let offset = -this.trackWidth/2; offset <= this.trackWidth/2; offset += 5) { // Dense 5px steps
                const normalAngle = angle + Math.PI / 2;
                const x = baseX + Math.cos(normalAngle) * offset;
                const y = baseY + Math.sin(normalAngle) * offset;
                
                if (this.isInBounds(x, y)) {
                    this.validPositions.push({ x, y });
                }
            }
        }
        
        return {
            type: 'oval',
            validPositions: this.validPositions,
            startZone: { x: centerX, y: centerY - radiusY },
            trackWidth: this.trackWidth
        };
    }

    /**
     * Generate a figure-8 track
     */
    generateFigure8Track() {
        this.validPositions = [];
        const centerX = this.width / 2;
        const centerY = this.height / 2;
        const radius = 320; // Reduced to fit in 80% area
        
        // Top loop - reduced offset to fit in 80% area
        for (let angle = 0; angle < Math.PI * 2; angle += 0.05) { // Reduced density
            const baseX = centerX + Math.cos(angle) * radius;
            const baseY = centerY - 160 + Math.sin(angle) * radius; // Reduced from 200
            
            for (let offset = -this.trackWidth/2; offset <= this.trackWidth/2; offset += 5) { // Dense sampling
                const normalAngle = angle + Math.PI / 2;
                const x = baseX + Math.cos(normalAngle) * offset;
                const y = baseY + Math.sin(normalAngle) * offset;
                
                if (this.isInBounds(x, y)) {
                    this.validPositions.push({ x, y });
                }
            }
        }
        
        // Bottom loop - reduced offset to fit in 80% area
        for (let angle = 0; angle < Math.PI * 2; angle += 0.05) { // Reduced density
            const baseX = centerX + Math.cos(angle) * radius;
            const baseY = centerY + 160 + Math.sin(angle) * radius; // Reduced from 200
            
            for (let offset = -this.trackWidth/2; offset <= this.trackWidth/2; offset += 5) { // Dense sampling
                const normalAngle = angle + Math.PI / 2;
                const x = baseX + Math.cos(normalAngle) * offset;
                const y = baseY + Math.sin(normalAngle) * offset;
                
                if (this.isInBounds(x, y)) {
                    this.validPositions.push({ x, y });
                }
            }
        }
        
        // Crossing section
        for (let y = centerY - 200; y <= centerY + 200; y += 10) { // Denser Y sampling
            for (let offset = -this.trackWidth/2; offset <= this.trackWidth/2; offset += 5) { // Dense sampling
                const x = centerX + offset;
                if (this.isInBounds(x, y)) {
                    this.validPositions.push({ x, y });
                }
            }
        }
        
        return {
            type: 'figure8',
            validPositions: this.validPositions,
            startZone: { x: centerX, y: centerY },
            trackWidth: this.trackWidth
        };
    }

    /**
     * Generate a circuit track with corners - FULLY WITHIN 80% AREA
     */
    generateCircuitTrack() {
        this.validPositions = [];
        
        // Use safe area bounds with extra margin to avoid edges completely
        const extraMargin = 100;
        const safeLeft = this.minX + extraMargin;
        const safeRight = this.maxX - extraMargin;
        const safeTop = this.minY + extraMargin;
        const safeBottom = this.maxY - extraMargin;
        
        const cornerRadius = 150;
        
        // Define circuit points WELL WITHIN the safe area
        const points = [
            { x: safeLeft + cornerRadius, y: safeTop + cornerRadius },
            { x: safeRight - cornerRadius, y: safeTop + cornerRadius },
            { x: safeRight - cornerRadius, y: safeBottom - cornerRadius },
            { x: safeLeft + cornerRadius, y: safeBottom - cornerRadius }
        ];
        
        // Generate track between points with rounded corners
        for (let i = 0; i < points.length; i++) {
            const start = points[i];
            const end = points[(i + 1) % points.length];
            
            // Straight section with dense sampling
            const dx = end.x - start.x;
            const dy = end.y - start.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const steps = distance / 10; // Increased density for continuity
            
            for (let step = 0; step < steps; step++) {
                const t = step / steps;
                const baseX = start.x + dx * t;
                const baseY = start.y + dy * t;
                
                // Add width perpendicular to direction
                const perpX = -dy / distance;
                const perpY = dx / distance;
                
                for (let offset = -this.trackWidth/2; offset <= this.trackWidth/2; offset += 5) { // Dense sampling
                    const x = baseX + perpX * offset;
                    const y = baseY + perpY * offset;
                    
                    if (this.isInBounds(x, y)) {
                        this.validPositions.push({ x, y });
                    }
                }
            }
            
            // Rounded corner with better positioning
            const cornerX = points[(i + 1) % points.length].x;
            const cornerY = points[(i + 1) % points.length].y;
            
            // Determine corner orientation and add extra fill
            const isRightCorner = (cornerX > this.width / 2);
            const isBottomCorner = (cornerY > this.height / 2);
            
            // Add rounded corner with dense sampling
            this.addRoundedCorner(cornerX, cornerY, cornerRadius);
            
            // Add extra positions around problematic corners
            if (isRightCorner && isBottomCorner) {
                // Bottom-right corner - add extra coverage
                for (let x = cornerX - cornerRadius - 50; x <= cornerX + 50; x += 5) {
                    for (let y = cornerY - cornerRadius - 50; y <= cornerY + 50; y += 5) {
                        if (this.isInBounds(x, y)) {
                            const dist = Math.sqrt((x - cornerX) ** 2 + (y - cornerY) ** 2);
                            if (dist <= cornerRadius + this.trackWidth/2) {
                                this.validPositions.push({ x, y });
                            }
                        }
                    }
                }
            }
        }
        
        return {
            type: 'circuit',
            validPositions: this.validPositions,
            startZone: { x: margin + 100, y: margin + 100 },
            trackWidth: this.trackWidth
        };
    }

    /**
     * Generate a serpentine/snake track
     */
    generateSerpentineTrack() {
        this.validPositions = [];
        
        // Keep serpentine well within safe bounds
        const amplitude = 250; // Reduced to stay in bounds
        const frequency = 3;
        const startX = this.minX + 100;
        const endX = this.maxX - 100;
        const length = endX - startX;
        const centerY = this.height / 2;
        
        for (let x = startX; x < startX + length; x += 10) { // Denser X sampling
            const t = (x - startX) / length;
            const baseY = centerY + Math.sin(t * Math.PI * 2 * frequency) * amplitude;
            
            // Add track width
            for (let offset = -this.trackWidth/2; offset <= this.trackWidth/2; offset += 5) { // Dense sampling
                const y = baseY + offset;
                
                if (this.isInBounds(x, y)) {
                    this.validPositions.push({ x, y });
                }
                
                // Also add some perpendicular width for curves
                const perpX = x + offset / 4;
                if (this.isInBounds(perpX, baseY)) {
                    this.validPositions.push({ x: perpX, y: baseY });
                }
            }
        }
        
        return {
            type: 'serpentine',
            validPositions: this.validPositions,
            startZone: { x: startX + 50, y: centerY },
            trackWidth: this.trackWidth
        };
    }

    /**
     * Generate a spiral track
     */
    generateSpiralTrack() {
        this.validPositions = [];
        const centerX = this.width / 2;
        const centerY = this.height / 2;
        const maxRadius = 350; // Reduced to stay well within 80% bounds
        const spirals = 3;
        
        for (let angle = 0; angle < Math.PI * 2 * spirals; angle += 0.08) { // Reduced density
            const t = angle / (Math.PI * 2 * spirals);
            const radius = 150 + t * maxRadius; // Start larger for wide track
            const baseX = centerX + Math.cos(angle) * radius;
            const baseY = centerY + Math.sin(angle) * radius;
            
            // Add track width  
            for (let offset = -this.trackWidth/2; offset <= this.trackWidth/2; offset += 5) { // Dense sampling
                const normalAngle = angle + Math.PI / 2;
                const x = baseX + Math.cos(normalAngle) * offset;
                const y = baseY + Math.sin(normalAngle) * offset;
                
                if (this.isInBounds(x, y)) {
                    this.validPositions.push({ x, y });
                }
            }
        }
        
        return {
            type: 'spiral',
            validPositions: this.validPositions,
            startZone: { x: centerX, y: centerY },
            trackWidth: this.trackWidth
        };
    }

    /**
     * Add a rounded corner to the track
     */
    addRoundedCorner(centerX, centerY, radius) {
        // Dense sampling for corners to prevent gaps
        for (let angle = 0; angle < Math.PI / 2; angle += 0.02) { // More angle steps
            for (let r = radius - this.trackWidth/2; r <= radius + this.trackWidth/2; r += 5) { // Dense 5px steps like straights
                const x = centerX + Math.cos(angle) * r;
                const y = centerY + Math.sin(angle) * r;
                
                if (this.isInBounds(x, y)) {
                    this.validPositions.push({ x, y });
                }
            }
        }
        
        // Fill corner area completely to prevent stuck issues
        const cornerPadding = 50;
        for (let x = centerX - cornerPadding; x <= centerX + radius + cornerPadding; x += 5) {
            for (let y = centerY - cornerPadding; y <= centerY + radius + cornerPadding; y += 5) {
                const distFromCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
                if (distFromCenter >= radius - this.trackWidth/2 && 
                    distFromCenter <= radius + this.trackWidth/2 &&
                    x >= centerX && y >= centerY &&
                    this.isInBounds(x, y)) {
                    this.validPositions.push({ x, y });
                }
            }
        }
    }

    /**
     * Check if position is within bounds
     */
    isInBounds(x, y) {
        // Use the 80% safe area bounds - tracks never go near edges
        return x >= this.minX && x <= this.maxX && 
               y >= this.minY && y <= this.maxY;
    }

    /**
     * Build spatial grid for O(1) collision detection
     */
    buildSpatialGrid() {
        const gridSize = 50; // 50x50 pixel cells
        this.positionGrid = {};
        this.gridSize = gridSize;
        
        // Add all valid positions to grid
        for (const pos of this.validPositions) {
            const gridX = Math.floor(pos.x / gridSize);
            const gridY = Math.floor(pos.y / gridSize);
            const key = `${gridX},${gridY}`;
            
            if (!this.positionGrid[key]) {
                this.positionGrid[key] = [];
            }
            this.positionGrid[key].push(pos);
        }
        
        console.log(`[TrackGenerator] Built spatial grid with ${Object.keys(this.positionGrid).length} cells`);
    }
    
    /**
     * Check if a position is on the generated track
     */
    isPositionValid(x, y) {
        // STRICT bounds check - no positions allowed outside safe area
        if (!this.isInBounds(x, y)) {
            return false;
        }
        
        // Use spatial grid for fast lookup if available
        if (this.positionGrid) {
            const gridSize = this.gridSize;
            // Slightly increased radius near edges for more forgiveness
            const edgeMargin = 150;
            const nearEdge = x < edgeMargin || x > this.width - edgeMargin || 
                           y < edgeMargin || y > this.height - edgeMargin;
            const checkRadius = nearEdge ? 160 : 145; // Larger radius near edges
            const checkRadiusSq = checkRadius * checkRadius;
            
            // Track if we found any close position
            let minDistanceSq = Infinity;
            
            // Check neighboring grid cells
            const gridX = Math.floor(x / gridSize);
            const gridY = Math.floor(y / gridSize);
            
            for (let gx = gridX - 4; gx <= gridX + 4; gx++) {
                for (let gy = gridY - 4; gy <= gridY + 4; gy++) {
                    const key = `${gx},${gy}`;
                    const positions = this.positionGrid[key];
                    
                    if (positions) {
                        for (const pos of positions) {
                            const dx = x - pos.x;
                            const dy = y - pos.y;
                            const distSq = dx * dx + dy * dy;
                            
                            if (distSq < minDistanceSq) {
                                minDistanceSq = distSq;
                            }
                            
                            // Early exit if we found a close enough position
                            if (distSq <= checkRadiusSq) {
                                return true;
                            }
                        }
                    }
                }
            }
            return false;
        }
        
        // Fallback: improved sampling (check more positions)
        const checkRadius = 145; // Match visual track surface radius exactly
        const checkRadiusSq = checkRadius * checkRadius;
        
        // Check every 10th position instead of every 50th
        const step = Math.max(1, Math.floor(this.validPositions.length / 500));
        for (let i = 0; i < this.validPositions.length; i += step) {
            const pos = this.validPositions[i];
            const dx = x - pos.x;
            const dy = y - pos.y;
            if ((dx * dx + dy * dy) <= checkRadiusSq) {
                return true;
            }
        }
        return false;
    }

    /**
     * Check if a position is in a corner (danger zone for getting stuck)
     */
    isCornerPosition(x, y) {
        const cornerMargin = 150;
        const nearLeft = x < cornerMargin;
        const nearRight = x > this.width - cornerMargin;
        const nearTop = y < cornerMargin;
        const nearBottom = y > this.height - cornerMargin;
        
        // True if near two edges (corner)
        return (nearLeft || nearRight) && (nearTop || nearBottom);
    }
    
    /**
     * Get a random valid position on the track
     */
    getRandomPosition() {
        if (this.validPositions.length === 0) {
            return { x: this.width / 2, y: this.height / 2 };
        }
        
        // Try to avoid corner positions
        const nonCornerPositions = this.validPositions.filter(pos => 
            !this.isCornerPosition(pos.x, pos.y)
        );
        
        // Use non-corner positions if available
        const positions = nonCornerPositions.length > 0 ? nonCornerPositions : this.validPositions;
        const index = Math.floor(Math.random() * positions.length);
        return positions[index];
    }

    /**
     * Get valid positions for spawning multiple players - GROUPED TOGETHER AT START
     */
    getStartPositions(count = 6) {
        const positions = [];
        
        // Find a good starting position (center of the track or a specific area)
        const centerX = this.width / 2;
        const centerY = this.height / 2;
        
        // Find valid positions near the center for grouped start
        let startCenter = null;
        let minDistance = Infinity;
        
        // Find the valid position closest to center-left of screen (good starting spot)
        const idealStartX = this.width * 0.2; // Start on left side
        const idealStartY = this.height * 0.5; // Middle height
        
        for (const pos of this.validPositions) {
            const distance = Math.sqrt(
                (pos.x - idealStartX) ** 2 + (pos.y - idealStartY) ** 2
            );
            if (distance < minDistance) {
                minDistance = distance;
                startCenter = pos;
            }
        }
        
        // If no valid position found, use first available
        if (!startCenter && this.validPositions.length > 0) {
            startCenter = this.validPositions[Math.floor(this.validPositions.length * 0.1)];
        }
        
        // Fallback to center if still no position
        if (!startCenter) {
            startCenter = { x: centerX, y: centerY };
            console.warn('[TrackGenerator] Using fallback center position for start');
        }
        
        console.log(`[TrackGenerator] Grouping ${count} players at start position (${Math.round(startCenter.x)}, ${Math.round(startCenter.y)})`);
        
        // Create tight formation around the start center
        // Arrange players in a 2x3 grid for 6 players
        const spacing = 60; // Tight spacing between players
        const rows = 2;
        const cols = 3;
        
        for (let i = 0; i < count; i++) {
            const row = Math.floor(i / cols);
            const col = i % cols;
            
            // Calculate position in grid formation
            const offsetX = (col - 1) * spacing; // -1, 0, 1 for 3 columns
            const offsetY = (row - 0.5) * spacing; // -0.5, 0.5 for 2 rows
            
            // Add some small random variation to prevent exact overlap
            const randomX = (Math.random() - 0.5) * 10;
            const randomY = (Math.random() - 0.5) * 10;
            
            positions.push({
                x: startCenter.x + offsetX + randomX,
                y: startCenter.y + offsetY + randomY
            });
        }
        
        console.log(`[TrackGenerator] Created ${positions.length} grouped start positions`);
        return positions;
    }

    /**
     * Draw the track on a graphics object (for visualization)
     */
    drawTrack(graphics) {
        if (!graphics || this.validPositions.length === 0) return;
        
        // Draw track surface as a road
        graphics.fillStyle(0x808080, 0.4); // Gray road surface, semi-transparent
        for (const pos of this.validPositions) {
            graphics.fillCircle(pos.x, pos.y, 10); // Larger circles for road surface
        }
        
        // Draw track edge lines
        graphics.lineStyle(6, 0xffffff, 0.6); // White edge lines
        const boundary = this.getTrackBoundary();
        if (boundary.length > 0) {
            graphics.beginPath();
            graphics.moveTo(boundary[0].x, boundary[0].y);
            for (let i = 1; i < boundary.length; i++) {
                graphics.lineTo(boundary[i].x, boundary[i].y);
            }
            graphics.closePath();
            graphics.strokePath();
        }
        
        // Draw center line (dashed)
        graphics.lineStyle(3, 0xffff00, 0.5); // Yellow center line
        const centerLine = this.getTrackCenterLine();
        for (let i = 0; i < centerLine.length - 1; i += 2) {
            graphics.beginPath();
            graphics.moveTo(centerLine[i].x, centerLine[i].y);
            if (centerLine[i + 1]) {
                graphics.lineTo(centerLine[i + 1].x, centerLine[i + 1].y);
            }
            graphics.strokePath();
        }
    }
    
    /**
     * Get the center line of the track
     */
    getTrackCenterLine() {
        const centerLine = [];
        const step = Math.max(1, Math.floor(this.validPositions.length / 50));
        
        for (let i = 0; i < this.validPositions.length; i += step) {
            centerLine.push(this.validPositions[i]);
        }
        
        return centerLine;
    }
    
    /**
     * Get the outer boundary of the track (for visualization)
     */
    getTrackBoundary() {
        // Simple boundary detection - get edge points
        const boundary = [];
        const step = Math.PI / 20;
        
        for (let angle = 0; angle < Math.PI * 2; angle += step) {
            let maxDist = 0;
            let boundaryPoint = null;
            
            for (const pos of this.validPositions) {
                const dist = Math.sqrt(
                    Math.pow(pos.x - this.width/2, 2) + 
                    Math.pow(pos.y - this.height/2, 2)
                );
                const posAngle = Math.atan2(
                    pos.y - this.height/2, 
                    pos.x - this.width/2
                );
                
                if (Math.abs(posAngle - angle) < step && dist > maxDist) {
                    maxDist = dist;
                    boundaryPoint = pos;
                }
            }
            
            if (boundaryPoint) {
                boundary.push(boundaryPoint);
            }
        }
        
        return boundary;
    }
}